import user_api from './api/users';
import Bugsnag from '@bugsnag/js';

/**
 * Action Logger Helper
 * Provides a centralized way to log user actions throughout the application
 * 
 * @fileoverview This helper abstracts the repetitive action logging pattern used throughout 
 * the application. It handles error reporting, provides consistent logging structure, and 
 * offers both individual function calls and pre-configured user loggers.
 * 
 * @example
 * // Basic usage with individual functions
 * import { logAction, ACTION_CATEGORIES } from 'utils/actionLogger';
 * 
 * await logAction({
 *   user: user._id,
 *   company: user.company,
 *   userDisplay: { name: user.displayName, email: user.email },
 *   role: user.role,
 *   action: 'Created Announcement',
 *   category: ACTION_CATEGORIES.ANNOUNCEMENTS,
 *   details: 'Successfully created announcement "Holiday Notice"'
 * });
 * 
 * @example
 * // Recommended usage with pre-configured user logger
 * import { createUserLogger, ACTION_CATEGORIES } from 'utils/actionLogger';
 * 
 * const logger = createUserLogger(user);
 * 
 * // Log successful action
 * await logger.logSuccess({
 *   action: 'Generated OTP',
 *   category: ACTION_CATEGORIES.OTP,
 *   details: 'Successfully generated 24-hour OTP for approvals'
 * });
 * 
 * // Log error action
 * await logger.logError({
 *   action: 'Failed to Delete User',
 *   category: ACTION_CATEGORIES.USER_MANAGEMENT,
 *   details: 'Failed to delete user due to database error'
 * });
 */

/**
 * Logs an action performed by a user
 * @param {Object} params - The logging parameters
 * @param {string} params.user - User ID
 * @param {string} params.company - Company name
 * @param {Object} params.userDisplay - User display information
 * @param {string} params.userDisplay.name - User display name
 * @param {string} params.userDisplay.email - User email
 * @param {number} params.role - User role (used to determine role display)
 * @param {string} params.action - The action performed
 * @param {string} params.category - The category of the action
 * @param {string} params.details - Detailed description of the action
 * @param {string} [params.status='success'] - Status of the action ('success', 'error', 'warning')
 * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
 * @returns {Promise<void>}
 */
export const logAction = async ({
  user,
  company,
  userDisplay,
  role,
  action,
  category,
  details,
  status = 'success',
  suppressConsoleLog = false
}) => {
  try {
    // Validate required parameters
    if (!user || !company || !userDisplay || !action || !category) {
      throw new Error('Missing required parameters for action logging');
    }

    const logData = {
      user,
      company,
      userDisplay: {
        name: userDisplay.name,
        email: userDisplay.email
      },
      role: role >= 3 ? 'Admin' : 'Store Manager',
      action,
      category,
      details,
      status
    };

    await user_api.post_action_log(logData);
  } catch (logError) {
    // Report error to Bugsnag
    Bugsnag.notify(logError);
    
    // Console log the error unless suppressed
    if (!suppressConsoleLog) {
      console.error('Error logging action:', logError);
    }
  }
};

/**
 * Logs a successful action
 * @param {Object} params - The logging parameters (same as logAction but without status)
 * @returns {Promise<void>}
 */
export const logSuccessAction = async (params) => {
  return logAction({ ...params, status: 'success' });
};

/**
 * Logs an error action
 * @param {Object} params - The logging parameters (same as logAction but without status)
 * @returns {Promise<void>}
 */
export const logErrorAction = async (params) => {
  return logAction({ ...params, status: 'error' });
};

/**
 * Logs a warning action
 * @param {Object} params - The logging parameters (same as logAction but without status)
 * @returns {Promise<void>}
 */
export const logWarningAction = async (params) => {
  return logAction({ ...params, status: 'warning' });
};

/**
 * Logs an info action
 * @param {Object} params - The logging parameters (same as logAction but without status)
 * @returns {Promise<void>}
 */
export const logInfoAction = async (params) => {
  return logAction({ ...params, status: 'info' });
};

/**
 * Creates a pre-configured logger for a specific user
 * @param {Object} user - User object containing _id, company, displayName, email, role
 * @returns {Object} - Object with logging methods pre-configured for the user
 */
export const createUserLogger = (user) => {
  const baseParams = {
    user: user._id,
    company: user.company,
    userDisplay: {
      name: user.displayName,
      email: user.email
    },
    role: user.role
  };

  return {
    /**
     * Log an action for this user
     * @param {Object} params - Action parameters
     * @param {string} params.action - The action performed
     * @param {string} params.category - The category of the action
     * @param {string} params.details - Detailed description of the action
     * @param {string} [params.status='success'] - Status of the action
     * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
     * @returns {Promise<void>}
     */
    log: async ({ action, category, details, status = 'success', suppressConsoleLog = false }) => {
      return logAction({
        ...baseParams,
        action,
        category,
        details,
        status,
        suppressConsoleLog
      });
    },

    /**
     * Log a successful action for this user
     * @param {Object} params - Action parameters
     * @param {string} params.action - The action performed
     * @param {string} params.category - The category of the action
     * @param {string} params.details - Detailed description of the action
     * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
     * @returns {Promise<void>}
     */
    logSuccess: async ({ action, category, details, suppressConsoleLog = false }) => {
      return logSuccessAction({
        ...baseParams,
        action,
        category,
        details,
        suppressConsoleLog
      });
    },

    /**
     * Log an error action for this user
     * @param {Object} params - Action parameters
     * @param {string} params.action - The action performed
     * @param {string} params.category - The category of the action
     * @param {string} params.details - Detailed description of the action
     * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
     * @returns {Promise<void>}
     */
    logError: async ({ action, category, details, suppressConsoleLog = false }) => {
      return logErrorAction({
        ...baseParams,
        action,
        category,
        details,
        suppressConsoleLog
      });
    },

    /**
     * Log a warning action for this user
     * @param {Object} params - Action parameters
     * @param {string} params.action - The action performed
     * @param {string} params.category - The category of the action
     * @param {string} params.details - Detailed description of the action
     * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
     * @returns {Promise<void>}
     */
    logWarning: async ({ action, category, details, suppressConsoleLog = false }) => {
      return logWarningAction({
        ...baseParams,
        action,
        category,
        details,
        suppressConsoleLog
      });
    },

    /**
     * Log an info action for this user
     * @param {Object} params - Action parameters
     * @param {string} params.action - The action performed
     * @param {string} params.category - The category of the action
     * @param {string} params.details - Detailed description of the action
     * @param {boolean} [params.suppressConsoleLog=false] - Whether to suppress console logging on error
     * @returns {Promise<void>}
     */
    logInfo: async ({ action, category, details, suppressConsoleLog = false }) => {
      return logInfoAction({
        ...baseParams,
        action,
        category,
        details,
        suppressConsoleLog
      });
    }
  };
};

/**
 * Common action categories - use these for consistency
 */
export const ACTION_CATEGORIES = {
  AUTHENTICATION: 'Authentication',
  USER_MANAGEMENT: 'User Management',
  TIME_ADJUSTMENT: 'Time Adjustment',
  BREAKLIST: 'Breaklist',
  SCHEDULE: 'Schedule',
  PREMIUM_VALUES: 'Premium Values',
  REPORTS: 'Reports',
  QR_CODE: 'QR Code',
  OTP: 'OTP',
  ANNOUNCEMENTS: 'Announcements',
  REQUIRED_DOCUMENTS: 'Required Documents',
  COMPANY_CONTENT: 'Company Content',
  ACCOUNT_MANAGEMENT: 'Account Management',
  STORE_CONFIGURATION: 'Store Configuration',
  HOLIDAY_MANAGEMENT: 'Holiday Management',
  OTHER: 'Other'
};

/**
 * Common action statuses - use these for consistency
 */
export const ACTION_STATUSES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

export default {
  logAction,
  logSuccessAction,
  logErrorAction,
  logWarningAction,
  logInfoAction,
  createUserLogger,
  ACTION_CATEGORIES,
  ACTION_STATUSES
}; 